/**
 * ABR filter delete confirmation dialog.
 */

import { ErrorBanner } from '@core/components';
import type { AbrFilter } from '@moderation/domain/AbrFilter';
import { useDeleteAbrFilter } from '@moderation/hooks';
import { Button, ButtonType, Dialog, Text } from '@soteria/design-system-ve';
import { useCallback } from 'react';

import * as styles from './AbrFilterDeleteDialog.css';

interface AbrFilterDeleteDialogProps {
  communityId: string;
  filter: AbrFilter;
  isOpen: boolean;
  onClose: () => void;
}

export function AbrFilterDeleteDialog({ communityId, filter, isOpen, onClose }: AbrFilterDeleteDialogProps) {
  const { deleteFilter, loading, errors, resetErrors } = useDeleteAbrFilter(communityId);

  const handleDelete = useCallback(async () => {
    const success = await deleteFilter(filter.id);

    if (success) {
      onClose();
    }
  }, [filter.id, deleteFilter, onClose]);

  const handleClose = useCallback(() => {
    resetErrors();
    onClose();
  }, [resetErrors, onClose]);

  return (
    <Dialog isOpen={isOpen} title="Delete Queue Filter" onClose={handleClose}>
      {errors && errors.length > 0 && <ErrorBanner errors={errors} title="Failed to delete filter:" />}

      <Text>
        Are you sure you want to delete <strong>{filter.name}</strong>? This action cannot be undone.
      </Text>

      <div className={styles.dialogFooter}>
        <Button buttonType={ButtonType.Secondary} onClick={handleClose}>
          Cancel
        </Button>
        <Button buttonType={ButtonType.Danger} disabled={loading} onClick={handleDelete}>
          {loading ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </Dialog>
  );
}
